import { createElement, memo, useEffect, useState } from 'react';
import { useWorkspaceItemContext } from '../../../app/contexts/workspace/workspace-item-context';
import { useAppSelector } from '../../../app/hooks/store-hooks';
import { MapTooltipData, MapTooltipInfo } from '../../../services/map-interaction-service';
import { AirportTooltip } from './AirportTooltip';
import { DiversionAirportTooltip } from './DiversionAirportTooltip';
import { FlightActiveTooltip } from './FlightActiveTooltip';
import { FlightParkedTooltip } from './FlightParkedTooltip';
import { FlightPreDepTooltip } from './FlightPreDepTooltip';
import { FlownPointTooltip } from './FlownPointTooltip';
import { ImpactsTooltip } from './ImpactsTooltip';
import { MapTooltip } from './MapTooltip';
import { CityPairTooltip } from './CityPairTooltip';
import { NavDataTooltip } from './NavDataTooltip';
import { RvrTooltip } from './RvrTooltip';
import { WeatherTooltip } from './WeatherTooltip';

export function Tooltips() {
    const tooltipSettings = useAppSelector((x) => x.settings.tooltipSettings);
    const context = useWorkspaceItemContext();

    useEffect(() => {
        // Sync settings with the interaction service
        // TODO: rewrite this service to be a normal react component
        context.interactionService.updateSettings(tooltipSettings);
    }, [tooltipSettings, context]);

    const [tooltipInfo, setTooltipInfo] = useState<MapTooltipInfo | null>(null);

    useEffect(() => {
        return context.interactionService.on('tooltip', (e) => {
            setTooltipInfo(e.data);
        }).unsubscribe;
    }, []);

    if (!tooltipInfo) {
        return null;
    }

    // Only scroll weather tooltips if it's alone
    const scrollable = tooltipInfo.tooltips.length === 1 && tooltipInfo.tooltips[0].kind === 'weather';

    return (
        <MapTooltip placement="bottom" position={tooltipInfo.event} scrollable={scrollable}>
            <TooltipList data={tooltipInfo.tooltips} scrollable={scrollable} />
        </MapTooltip>
    );
}

const TooltipList = memo((props: { data: MapTooltipData[]; scrollable: boolean }) => {
    return (
        <div className="d-flex flex-column gap-1 align-items-center">
            {filterTooltips(props.data).map((tooltip) => (
                // k-card class is only applied for non-scrollable tooltips. Check comment in MapTooltip.tsx for details.
                <div className={!props.scrollable ? 'k-card' : ''} key={tooltip.kind}>
                    {createElement(tooltipComponents[tooltip.kind] as React.FC<{ info: MapTooltipData }>, { info: tooltip })}
                </div>
            ))}
        </div>
    );
});

function filterTooltips(data: MapTooltipData[]): MapTooltipData[] {
    if (!data.length) {
        return data;
    }

    // If there are airports in the tooltip combine them with flights
    if (data.some((x) => x.kind === 'airport')) {
        return data.filter((x) => x.kind === 'airport' || x.kind === 'flight');
    }

    // Only how the first one
    return [data[0]];
}

type TooltipComponents = {
    [K in MapTooltipData['kind']]: React.FC<{ info: Extract<MapTooltipData, { kind: K }> }>;
};

const tooltipComponents: TooltipComponents = {
    weather: WeatherTooltip,
    'flown-point': FlownPointTooltip,
    airport: AirportTooltip,
    flight: FlightActiveTooltip,
    'pre-dep-flight': FlightPreDepTooltip,
    'parked-flight': FlightParkedTooltip,
    navdata: NavDataTooltip,
    diversion: DiversionAirportTooltip,
    impacts: ImpactsTooltip,
    rvr: RvrTooltip,
    'city-pair': CityPairTooltip
};
